import { CameraController } from "./core/camera_controller.js";
import { Renderer } from "./core/renderer.js";
import { PhysicsInterpolation } from "./physics/physics_interpolation.js";
import { SceneManager } from "./core/scene_manager.js";
import { FontLoader } from "./fonts/font_loader.js";
import { LocalizationManager } from "./localization/localization_manager.js";
import { GlobalSettings } from "./global_settings.js";
import { fillAllImagesInfo } from "./image_loader/all_images_info.js";
import { ImgID } from "./image_loader/img_id.js";
import { ImagesHolder } from "./image_loader/images_holder.js";
import { NineSliceDrawer } from "./image_loader/nine_slicer/nine_slice_drawer.js";
import { LayoutScalerLocal } from "./layout_scaler/layout_scaler.js";
import { BackButtonHandler } from "./navigation/back_button_handler.js";
import { PhysicsWorld } from "./physics/physics_world.js";
import { fillAllResourcesInfo } from "./resource_loader/all_resources_info.js";
import { ResourceManager } from "./resource_loader/resource_manager.js";
import { ScreenManager, Screens } from "./screen_manager/screen_manager.js";
import { StatisticsStorage } from "./storage/statistics_storage.js";
import { TelegramAPISettings } from "./telegram_api_settings.js";
import { DialogManager } from "./ui/dialog/dialog_manager.js";
import { OverlayManager } from "./ui/overlay/overlay_manager.js";
import { addActiveCoinTransitionBadge } from "./ui/overlay/coin_transition_badge.js";
import { UIElementHolder } from "./ui/common_ui_elements/ui_element_holder.js";
import { NavigationBlock } from "./ui/navigation_block/navigation_block.js";
import { LeaderboardScreen } from "./ui/screens/leaderboard/leaderboard_screen.js";
import { FriendsScreen } from "./ui/screens/friends_screen/friends_screen.js";
import { GameScreen } from "./ui/screens/game_screen/game_screen.js";
import { GameUI } from "./ui/screens/game_screen/ui/game_ui.js";
import { WebGLUIRuntime } from "./webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "./webgl_2d/vfx_2d_runtime.js";
import { Vfx2DGlobalAtlas } from "./webgl_2d/core/rendering/textures/vfx_2d_global_atlas.js";
import { VFX_2D_ATLAS_CONFIG, VFX_2D_ATLAS_SIZE } from "./webgl_2d/vfx_2d_atlas_config.js";
import { LoadingScreen } from "./ui/screens/loading_screen/loading_screen.js";
import { LoadingGameScreen } from "./ui/screens/loading_game_screen.ts/loading_game_screen.js";
import type { LoadingGameVariant } from "./ui/screens/loading_game_screen.ts/loading_game_screen.js";
import { MenuScreen } from "./ui/screens/menu_screen/menu_screen.js";
import { SettingsDialog } from "./ui/dialog/settings_dialog.js";
import { AvatarSelectionDialog } from "./ui/dialog/avatar_selection_dialog.js";
import { LeaveLevelDialog } from "./ui/dialog/leave_level_dialog.js";
import { MoreLivesDialog } from "./ui/dialog/more_lives_dialog.js";
import { PlayDialog } from "./ui/dialog/play_dialog.js";
import { ProfileDialog } from "./ui/dialog/profile_dialog.js";
import { FpsOverlay } from "./debug/fps_overlay.js";
import { PhysicsDebugRenderer } from "./debug/physics_debug_renderer.js";
import { AudioController, audioController } from "./audio/audio_controller.js";
import { isMobileDevice } from "./utils/device_utils.js";
import { SimulationContext } from "./simulation/simulation_context.js";
import { TestLevelBridge } from "./debug/test_level_bridge.js";
import { runStabilityBatch } from "./debug/stability_check.js";
import { runSimulationBatch, runSimulationCollector } from "./debug/simulation_batch.js";
import { UpdateRequiredDialog, UpdateRequiredMode } from "./ui/dialog/update_required_dialog.js";
import { OverlayId } from "./ui/overlay/overlay_ids.js";
import { OverlayFactory } from "./ui/overlay/overlay_factory.js";
import { GameResultOverlayParams } from "./ui/overlay/overlays/game_result_overlay.js";
import { Vector2 } from "./common/vector2.js";
import { TriangleBench } from "./debug/triangle_bench.js";
import { NetworkManager } from "./network_manager.js";
import { isNetworkishError } from "./utils/network_errors.js";
import { TooltipManager } from "./ui/tooltip/tooltip_manager.js";
import { AvatarUtils } from "./common/avatar_utils.js";
import { HappyFallScreen } from "./ui/screens/happy_fall_screen/happy_fall_screen.js";
import { BuyBoosterDialog, BuyBoosterType } from "./ui/dialog/buy_booster_dialog.js";
import { CumulativeBoosterID } from "./cumulative_boosters/cumulative_booster_id.js";
import { isMemoryConstrained } from "./utils/memory_policy.js";
import { releaseHiddenCanvas } from "./utils/canvas_memory.js";
import { crashCapture } from "./crash/crash_reporter.js";

export class Game
{
  public static instance: Game;

  private static readonly Y_OVERFLOW = 1; // hack for Telegram issue

  public static max_physics_steps = 3;
  public static physics_budget_ms = 8;
  public static physics_adaptive = true;
  public static physics_slow_mode = false;
  public static physics_slow_enter_ms = 8;
  public static physics_slow_exit_ms = 4;

  private vfx_main_ui_back_canvas: HTMLCanvasElement;
  private game_canvas: HTMLCanvasElement;
  private tooltip_canvas: HTMLCanvasElement;
  private menu_canvas: HTMLCanvasElement;
  private leaderboard_canvas: HTMLCanvasElement;
  private friends_canvas: HTMLCanvasElement;
  private webgl_ui_main_canvas: HTMLCanvasElement;
  private vfx_main_ui_front_canvas: HTMLCanvasElement;
  private navigation_canvas: HTMLCanvasElement;
  private loading_canvas: HTMLCanvasElement;
  private dialog_canvas: HTMLCanvasElement;
  private vfx_overlay_back_canvas: HTMLCanvasElement;
  private vfx_overlay_front_canvas: HTMLCanvasElement;
  private overlay_canvas: HTMLCanvasElement;

  private game_ctx: CanvasRenderingContext2D;
  private tooltip_ctx: CanvasRenderingContext2D;
  private menu_ctx: CanvasRenderingContext2D;
  private leaderboard_ctx: CanvasRenderingContext2D;
  private friends_ctx: CanvasRenderingContext2D;
  private navigation_ctx: CanvasRenderingContext2D;
  private loading_ctx!: CanvasRenderingContext2D;
  private dialog_ctx: CanvasRenderingContext2D;
  private overlay_ctx: CanvasRenderingContext2D;

  private game_ui?: GameUI;
  private happy_fall_screen?: HappyFallScreen;
  private menu_screen?: MenuScreen;
  private leaderboard_screen?: LeaderboardScreen;
  private friends_screen?: FriendsScreen;
  private navigation_block?: NavigationBlock;
  private loading_screen?: LoadingScreen;
  private loading_game_screen?: LoadingGameScreen;
  private loading_game_screen_shown_at = 0;
  private loading_game_play: ( ( play: boolean ) => void ) | null = null;
  private static readonly MAX_SCENE_REVEAL_HOLD_MS = 2500;
  private scene_reveal_hold_until = 0;
  private scene_revealed = true;
  private menu_startup_ready: Promise<void> = Promise.resolve();

  private static SCREEN_WIDTH: number;
  private static SCREEN_HEIGHT: number;

  private static SAFE_AREA_TOP: number = 0;
  private static SAFE_AREA_BOTTOM: number = 0;
  private static SAFE_AREA_LEFT: number = 0;
  private static SAFE_AREA_RIGHT: number = 0;

  private static HORIZONTAL_W_TO_H_RATIO: number = 0.75;

  private game_x = 0;

  private static is_mobile: boolean;

  private static dots_per_pixel: number;

  /** Delays only the first menu render until its server-backed event state is ready. */
  public setMenuStartupReadyPromise( promise: Promise<void> ): void
  {
    this.menu_startup_ready = promise;
  }

  constructor(
    screen_width: number = window.innerWidth,
    screen_height: number = window.innerHeight,
  )
  {
    Game.instance = this;
    Game.is_mobile = isMobileDevice();

    const horizontal_screen: boolean = screen_width / screen_height > Game.HORIZONTAL_W_TO_H_RATIO;
    this.game_x = horizontal_screen ? Math.round( ( screen_width - screen_height * Game.HORIZONTAL_W_TO_H_RATIO ) / 2 ) : 0;

    if ( horizontal_screen )
    {
      screen_width = Math.round( screen_height * Game.HORIZONTAL_W_TO_H_RATIO );
    }

    Game.dots_per_pixel = window.devicePixelRatio || 1;
    const dpr = Game.dots_per_pixel;

    window.scrollTo( 0, 0 );

    const safe_area_insets = TelegramAPISettings.getSafeAreaInsets();
    Game.SAFE_AREA_TOP = safe_area_insets.top;
    Game.SAFE_AREA_BOTTOM = safe_area_insets.bottom;
    Game.SAFE_AREA_LEFT = safe_area_insets.left;
    Game.SAFE_AREA_RIGHT = safe_area_insets.right;

    const logical_screen_w = screen_width;
    const logical_screen_h = screen_height;

    const screen_w = Math.round( dpr * screen_width );
    const screen_h = Math.round( dpr * screen_height );

    Game.SCREEN_WIDTH = logical_screen_w;
    Game.SCREEN_HEIGHT = logical_screen_h;

    this.vfx_main_ui_back_canvas = document.getElementById( "vfxMainUIBackCanvas" ) as HTMLCanvasElement;
    this.game_canvas = document.getElementById( "gameUICanvas" ) as HTMLCanvasElement;
    this.tooltip_canvas = document.getElementById( "tooltipCanvas" ) as HTMLCanvasElement;
    this.menu_canvas = document.getElementById( "menuCanvas" ) as HTMLCanvasElement;
    this.navigation_canvas = document.getElementById( "navigationCanvas" ) as HTMLCanvasElement;
    this.leaderboard_canvas = document.getElementById( "leaderboardCanvas" ) as HTMLCanvasElement;
    this.friends_canvas = document.getElementById( "friendsCanvas" ) as HTMLCanvasElement;
    this.webgl_ui_main_canvas = document.getElementById( "webglUIMainCanvas" ) as HTMLCanvasElement;
    this.vfx_main_ui_front_canvas = document.getElementById( "vfxMainUIFrontCanvas" ) as HTMLCanvasElement;
    this.loading_canvas = document.getElementById( "loadingCanvas" ) as HTMLCanvasElement;
    this.dialog_canvas = document.getElementById( "dialogCanvas" ) as HTMLCanvasElement;
    this.vfx_overlay_back_canvas = document.getElementById( "vfxOverlayBackCanvas" ) as HTMLCanvasElement;
    this.vfx_overlay_front_canvas = document.getElementById( "vfxOverlayFrontCanvas" ) as HTMLCanvasElement;
    this.overlay_canvas = document.getElementById( "overlayCanvas" ) as HTMLCanvasElement;

    const menu_screen_canvases = [
      this.menu_canvas,
    ];
    ScreenManager.registerMenuScreenCanvases( menu_screen_canvases, true );

    const game_screen_canvases = [
      this.game_canvas,
    ];
    ScreenManager.registerGameScreenCanvases( game_screen_canvases, false );

    ScreenManager.registerCanvas( Screens.HAPPY_FALL, this.menu_canvas, false );

    ScreenManager.registerCanvas( Screens.LEADERBOARD, this.leaderboard_canvas, true );
    ScreenManager.registerCanvas( Screens.FRIENDS, this.friends_canvas, true );

    this.vfx_main_ui_back_canvas.width = screen_w;
    this.vfx_main_ui_back_canvas.height = screen_h;
    this.vfx_main_ui_back_canvas.style.left = `${this.game_x}px`;
    this.vfx_main_ui_back_canvas.style.top = '0px';
    this.vfx_main_ui_back_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_main_ui_back_canvas.style.height = `${logical_screen_h}px`;
    this.vfx_main_ui_back_canvas.style.zIndex = '1';

    this.game_canvas.width = screen_w;
    this.game_canvas.height = screen_h;
    this.game_canvas.style.left = `${this.game_x}px`;
    this.game_canvas.style.top = '0px';
    this.game_canvas.style.width = `${logical_screen_w}px`;
    this.game_canvas.style.height = `${logical_screen_h}px`;
    this.game_canvas.style.zIndex = '2';

    this.tooltip_canvas.width = screen_w;
    this.tooltip_canvas.height = screen_h;
    this.tooltip_canvas.style.left = `${this.game_x}px`;
    this.tooltip_canvas.style.top = '0px';
    this.tooltip_canvas.style.width = `${logical_screen_w}px`;
    this.tooltip_canvas.style.height = `${logical_screen_h}px`;
    this.tooltip_canvas.style.zIndex = '2';

    this.menu_canvas.width = screen_w;
    this.menu_canvas.height = screen_h;
    this.menu_canvas.style.left = `${this.game_x}px`;
    this.menu_canvas.style.top = '0px';
    this.menu_canvas.style.width = `${logical_screen_w}px`;
    this.menu_canvas.style.height = `${logical_screen_h}px`;
    this.menu_canvas.style.zIndex = '2';

    this.leaderboard_canvas.width = screen_w;
    this.leaderboard_canvas.height = screen_h;
    this.leaderboard_canvas.style.left = `${this.game_x}px`;
    this.leaderboard_canvas.style.top = '0px';
    this.leaderboard_canvas.style.width = `${logical_screen_w}px`;
    this.leaderboard_canvas.style.height = `${logical_screen_h}px`;
    this.leaderboard_canvas.style.zIndex = '2';

    this.friends_canvas.width = screen_w;
    this.friends_canvas.height = screen_h;
    this.friends_canvas.style.left = `${this.game_x}px`;
    this.friends_canvas.style.top = '0px';
    this.friends_canvas.style.width = `${logical_screen_w}px`;
    this.friends_canvas.style.height = `${logical_screen_h}px`;
    this.friends_canvas.style.zIndex = '2';

    this.navigation_canvas.width = screen_w;
    this.navigation_canvas.height = screen_h;
    this.navigation_canvas.style.left = `${this.game_x}px`;
    this.navigation_canvas.style.top = '0px';
    this.navigation_canvas.style.width = `${logical_screen_w}px`;
    this.navigation_canvas.style.height = `${logical_screen_h}px`;
    this.navigation_canvas.style.zIndex = '3';

    this.webgl_ui_main_canvas.width = screen_w;
    this.webgl_ui_main_canvas.height = screen_h;
    this.webgl_ui_main_canvas.style.left = `${this.game_x}px`;
    this.webgl_ui_main_canvas.style.top = '0px';
    this.webgl_ui_main_canvas.style.width = `${logical_screen_w}px`;
    this.webgl_ui_main_canvas.style.height = `${logical_screen_h}px`;
    this.webgl_ui_main_canvas.style.zIndex = '3';

    this.vfx_main_ui_front_canvas.width = screen_w;
    this.vfx_main_ui_front_canvas.height = screen_h;
    this.vfx_main_ui_front_canvas.style.left = `${this.game_x}px`;
    this.vfx_main_ui_front_canvas.style.top = '0px';
    this.vfx_main_ui_front_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_main_ui_front_canvas.style.height = `${logical_screen_h}px`;
    this.vfx_main_ui_front_canvas.style.zIndex = '4';

    this.dialog_canvas.width = screen_w;
    this.dialog_canvas.height = screen_h;
    this.dialog_canvas.style.left = `${this.game_x}px`;
    this.dialog_canvas.style.top = '0px';
    this.dialog_canvas.style.width = `${logical_screen_w}px`;
    this.dialog_canvas.style.height = `${logical_screen_h}px`;

    this.vfx_overlay_back_canvas.width = screen_w;
    this.vfx_overlay_back_canvas.height = screen_h;
    this.vfx_overlay_back_canvas.style.left = `${this.game_x}px`;
    this.vfx_overlay_back_canvas.style.top = '0px';
    this.vfx_overlay_back_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_overlay_back_canvas.style.height = `${logical_screen_h}px`;

    this.vfx_overlay_front_canvas.width = screen_w;
    this.vfx_overlay_front_canvas.height = screen_h;
    this.vfx_overlay_front_canvas.style.left = `${this.game_x}px`;
    this.vfx_overlay_front_canvas.style.top = '0px';
    this.vfx_overlay_front_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_overlay_front_canvas.style.height = `${logical_screen_h}px`;

    this.overlay_canvas.width = screen_w;
    this.overlay_canvas.height = screen_h;
    this.overlay_canvas.style.left = `${this.game_x}px`;
    this.overlay_canvas.style.top = '0px';
    this.overlay_canvas.style.width = `${logical_screen_w}px`;
    this.overlay_canvas.style.height = `${logical_screen_h}px`;

    const ctx1 = this.menu_canvas.getContext( "2d" );
    const ctx2 = this.game_canvas.getContext( "2d" );
    const ctx3 = this.navigation_canvas.getContext( "2d" );
    const ctx5 = this.dialog_canvas.getContext( "2d" );
    const ctx6 = this.leaderboard_canvas.getContext( "2d" );
    const ctx7 = this.overlay_canvas.getContext( "2d" );
    const ctx10 = this.tooltip_canvas.getContext( "2d" );
    const ctx15 = this.friends_canvas.getContext( "2d" );

    if ( !ctx1 || !ctx2 || !ctx3 || !ctx5 || !ctx6 || !ctx7 || !ctx10 || !ctx15 )
      throw new Error( "Canvas 2D context not supported." );

    this.menu_ctx = ctx1;
    this.game_ctx = ctx2;
    this.navigation_ctx = ctx3;
    this.dialog_ctx = ctx5;
    this.leaderboard_ctx = ctx6;
    this.overlay_ctx = ctx7;
    this.tooltip_ctx = ctx10;
    this.friends_ctx = ctx15;

    if ( dpr !== 1 )
    {
      ctx1.scale( dpr, dpr );
      ctx2.scale( dpr, dpr );
      ctx3.scale( dpr, dpr );
      ctx5.scale( dpr, dpr );
      ctx6.scale( dpr, dpr );
      ctx7.scale( dpr, dpr );
      ctx10.scale( dpr, dpr );
      ctx15.scale( dpr, dpr );
    }

    this.initializeGlobalLayoutScaler();

    WebGLUIRuntime.init( this.webgl_ui_main_canvas );
    Vfx2DRuntime.init(
      this.vfx_main_ui_back_canvas,
      this.vfx_main_ui_front_canvas,
      this.vfx_overlay_back_canvas,
      this.vfx_overlay_front_canvas,
    );

    if ( isMemoryConstrained() )
    {
      for ( const canvas of [
        this.game_canvas, this.menu_canvas, this.leaderboard_canvas, this.friends_canvas,
      ] )
        this.releaseScreenCanvas( canvas );

      for ( const canvas of [ this.tooltip_canvas, this.dialog_canvas, this.overlay_canvas ] )
        releaseHiddenCanvas( canvas );
    }

    ScreenManager.addOnChangeListener( () => { this.current_screen_painted_once = false; } );
  }

  static getScreenWidth(): number { return this.SCREEN_WIDTH; }
  static getScreenHeight(): number { return this.SCREEN_HEIGHT; }
  static getDpr(): number { return this.dots_per_pixel; }
  static getGameX(): number { return this.instance.game_x; }

  // The overlay VFX canvases (coin-fly, confetti, fireworks, ...) are
  // normally shown/hidden by OverlayManager as part of its own overlay
  // stack. Screens that play VFX without going through OverlayManager (e.g.
  // AdventureBoxOpenPopup) need to unhide them themselves.
  // The three.js canvas is never part of ScreenManager's stack, so after
  // leaving the game it kept displaying the last rendered frame under every
  // 2D screen; any unpainted pixel above it showed stale game colors. Hiding
  // it also releases a full-screen compositor surface on non-game screens.
  public setGameSceneCanvasVisible( visible: boolean ): void
  {
    const canvas = Renderer.renderer?.domElement;
    if ( !canvas )
      return;

    const display = visible ? "block" : "none";
    if ( canvas.style.display !== display )
      canvas.style.display = display;

    if ( isMemoryConstrained() )
      Renderer.setBufferReleased( !visible );
  }

  public setVfxOverlayCanvasesVisible( visible: boolean ): void
  {
    const display = visible ? "block" : "none";
    this.vfx_overlay_back_canvas.style.display = display;
    this.vfx_overlay_front_canvas.style.display = display;
  }

  public static async initializeSafeAreas(): Promise<void>
  {
    const safe_area_insets = TelegramAPISettings.getSafeAreaInsets();
    Game.SAFE_AREA_TOP = safe_area_insets.top;
    Game.SAFE_AREA_BOTTOM = safe_area_insets.bottom;
    Game.SAFE_AREA_LEFT = safe_area_insets.left;
    Game.SAFE_AREA_RIGHT = safe_area_insets.right;
  }

  public updateTelegramLayout(): void
  {
    const previous_bottom = Game.SAFE_AREA_BOTTOM;
    const insets = TelegramAPISettings.getSafeAreaInsets();
    Game.SAFE_AREA_TOP = insets.top;
    Game.SAFE_AREA_BOTTOM = insets.bottom;
    Game.SAFE_AREA_LEFT = insets.left;
    Game.SAFE_AREA_RIGHT = insets.right;
    const content_origin = new Vector2( insets.left, insets.top );
    this.game_ui?.setPixelPos( content_origin, true );
    this.menu_screen?.setPixelPos( content_origin, true );
    this.leaderboard_screen?.setPixelPos( content_origin, true );
    this.friends_screen?.setPixelPos( content_origin, true );
    this.happy_fall_screen?.setPixelPos( content_origin, true );
    this.updateNavigationCanvas();
    if ( this.navigation_block && previous_bottom !== insets.bottom )
    {
      this.navigation_block.destroy();
      const navigation_height = LayoutScaler.heightFromDesign( 400 ) + insets.bottom;
      this.navigation_block = new NavigationBlock(
        this.navigation_ctx,
        insets.left,
        LayoutScaler.yFromDesign( 0 ),
        Game.getContentWidth(),
        navigation_height
      );
    }
    this.navigation_block?.updateScreenBlock();
    this.scheduleViewportResizeCheck();
    this.renderAll();
  }

  private viewport_resize_timer: number | null = null;

  private scheduleViewportResizeCheck(): void
  {
    if ( TestLevelBridge.isBatchActive() )
      return;

    if ( this.viewport_resize_timer !== null )
      window.clearTimeout( this.viewport_resize_timer );

    this.viewport_resize_timer = window.setTimeout( () =>
    {
      this.viewport_resize_timer = null;
      this.handleViewportResize();
    }, 300 );
  }

  // The whole canvas stack is sized once in the constructor, but the real
  // viewport can keep changing after startup: Safari collapses its toolbar,
  // Telegram expands the webapp. Every viewport row the stack no longer covers
  // shows the bare #0039B9 body background as a solid stripe, so the stack has
  // to follow the viewport.
  private handleViewportResize(): void
  {
    // The browser viewport is the authority for canvas coverage: some Telegram
    // clients under-report viewportHeight while the webview itself is taller,
    // and every webview row the stack does not cover shows the bare body
    // background regardless of what Telegram considers visible.
    // ceil: a fractional visual-viewport height rounded down would leave a
    // sub-pixel row of background visible along the bottom edge.
    const viewport_w = Math.ceil( window.visualViewport?.width ?? window.innerWidth );
    const viewport_h = Math.ceil( window.visualViewport?.height ?? window.innerHeight );

    if ( viewport_w < 50 || viewport_h < 50 )
      return;

    const horizontal_screen = viewport_w / viewport_h > Game.HORIZONTAL_W_TO_H_RATIO;
    const screen_width = horizontal_screen
      ? Math.round( viewport_h * Game.HORIZONTAL_W_TO_H_RATIO )
      : viewport_w;
    const screen_height = viewport_h;
    const dpr = window.devicePixelRatio || 1;

    if ( screen_width === Game.SCREEN_WIDTH
      && screen_height === Game.SCREEN_HEIGHT
      && dpr === Game.dots_per_pixel )
      return;

    Game.SCREEN_WIDTH = screen_width;
    Game.SCREEN_HEIGHT = screen_height;
    Game.dots_per_pixel = dpr;
    this.game_x = horizontal_screen ? Math.round( ( viewport_w - screen_width ) / 2 ) : 0;

    const buffer_w = Math.round( dpr * screen_width );
    const buffer_h = Math.round( dpr * screen_height );

    const all_canvases = [
      this.vfx_main_ui_back_canvas, this.vfx_main_ui_front_canvas, this.webgl_ui_main_canvas, this.game_canvas,
      this.tooltip_canvas, this.menu_canvas, this.leaderboard_canvas, this.friends_canvas,
      this.navigation_canvas, this.dialog_canvas,
      this.vfx_overlay_back_canvas, this.vfx_overlay_front_canvas, this.overlay_canvas,
    ];

    for ( const canvas of all_canvases )
    {
      canvas.style.left = `${this.game_x}px`;
      canvas.style.width = `${screen_width}px`;
      canvas.style.height = `${screen_height}px`;
    }

    const full_screen_2d = [
      { canvas: this.tooltip_canvas, ctx: this.tooltip_ctx },
      { canvas: this.dialog_canvas, ctx: this.dialog_ctx },
      { canvas: this.overlay_canvas, ctx: this.overlay_ctx },
    ];

    for ( const { canvas, ctx } of full_screen_2d )
    {
      canvas.width = buffer_w;
      canvas.height = buffer_h;
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      if ( dpr !== 1 )
        ctx.scale( dpr, dpr );
    }

    for ( const canvas of [ this.vfx_main_ui_back_canvas, this.vfx_main_ui_front_canvas, this.webgl_ui_main_canvas, this.vfx_overlay_back_canvas, this.vfx_overlay_front_canvas ] )
    {
      canvas.width = buffer_w;
      canvas.height = buffer_h;
    }
    Vfx2DRuntime.handleCanvasResize();

    const screen_canvases = [
      this.game_canvas, this.menu_canvas, this.leaderboard_canvas, this.friends_canvas,
    ];

    for ( const canvas of screen_canvases )
    {
      if ( canvas.width > 1 )
        this.allocateScreenCanvas( canvas );
    }

    this.initializeGlobalLayoutScaler();

    DialogManager.setScreenSize( screen_width, screen_height );
    OverlayManager.setScreenSize( screen_width, screen_height );

    Renderer.resize();

    this.updateNavigationCanvas();

    if ( this.navigation_block )
    {
      this.navigation_block.destroy();
      this.navigation_block = new NavigationBlock(
        this.navigation_ctx,
        Game.SAFE_AREA_LEFT,
        LayoutScaler.yFromDesign( 0 ),
        Game.getContentWidth(),
        LayoutScaler.heightFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM
      );
      this.navigation_block.updateScreenBlock();
    }

    if ( this.loading_screen || this.loading_game_screen )
    {
      this.setupLoadingCanvas( this.loading_screen !== undefined );
      if ( this.loading_screen )
      {
        this.loading_screen.cleanup();
        this.loading_screen = new LoadingScreen(
          this.loading_ctx,
          0,
          0,
          screen_width,
          screen_height + Game.safeAreaBottom()
        );
      }
      if ( this.loading_game_screen )
      {
        this.loading_game_screen.cleanup();
        this.loading_game_screen = new LoadingGameScreen( this.loading_ctx, 0, 0, screen_width, screen_height );
        if ( this.loading_game_play )
          this.loading_game_screen.showPlayButtons( play => this.chooseLoadingGamePlay( play ) );
      }
    }

    const current = ScreenManager.getCurrentScreen();

    if ( current === Screens.GAME )
    {
      // A running level cannot be rebuilt without restarting it, so the game
      // screen only gets its canvases and camera resized; the reallocated UI
      // canvas is blank until the tree repaints.
      this.game_ui?.forceRedraw();
    }
    else if ( current && !this.isLoadingGameScreenShown() && !this.loading_screen )
    {
      void ScreenManager.showScreen( current );
    }

    this.renderAll();
  }

  static safeAreaTop(): number { return this.SAFE_AREA_TOP; }
  static safeAreaBottom(): number { return this.SAFE_AREA_BOTTOM; }
  static safeAreaLeft(): number { return this.SAFE_AREA_LEFT; }
  static safeAreaRight(): number { return this.SAFE_AREA_RIGHT; }

  private initializeGlobalLayoutScaler(): void
  {
    console.table( {
      screen: { w: screen.width, h: screen.height },
      inner: { w: innerWidth, h: innerHeight },
      vv: { w: visualViewport?.width, h: visualViewport?.height },
      game: { w: Game.SCREEN_WIDTH, h: Game.SCREEN_HEIGHT },
      dpr: Game.dots_per_pixel
    } );
    const scaler = new LayoutScalerLocal( Game.SCREEN_WIDTH, Game.SCREEN_HEIGHT );

    globalThis.LayoutScaler = {
      widthFromDesign: ( design_px: number ) => scaler.widthFromDesign( design_px ),
      heightFromDesign: ( design_px: number ) => scaler.heightFromDesign( design_px ),
      xFromDesign: ( design_x_at_base: number ) => scaler.xFromDesign( design_x_at_base ),
      yFromDesign: ( design_y_at_base: number ) => scaler.yFromDesign( design_y_at_base ),
      widthFromDesignAdaptive: ( design_w_at_base: number ) => scaler.widthFromDesignAdaptive( design_w_at_base ),
      heightFromDesignAdaptive: ( design_h_at_base: number ) => scaler.heightFromDesignAdaptive( design_h_at_base ),
      xFromLayouts: ( x_min: number, x_max: number ) => scaler.xFromLayouts( x_min, x_max ),
      yFromLayouts: ( y_min: number, y_max: number ) => scaler.yFromLayouts( y_min, y_max ),
      widthFromLayouts: ( w_min: number, w_max: number ) => scaler.widthFromLayouts( w_min, w_max ),
      heightFromLayouts: ( h_min: number, h_max: number ) => scaler.heightFromLayouts( h_min, h_max ),
      widthFromHeightLayouts: ( w_min: number, w_max: number ) => scaler.widthFromHeightLayouts( w_min, w_max ),
      cornerRadiusFromDesign: ( design_radius: number ) => scaler.cornerRadiusFromDesign( design_radius ),
      getScale: () => scaler.getScale(),
      getVisibleDesignWidth: () => scaler.getVisibleDesignWidth(),
      getVisibleDesignHeight: () => scaler.getVisibleDesignHeight(),
      textSizeFromDesign: ( text_size: number ) => scaler.textSizeFromDesign( text_size ),
      textBorderSizeFromDesign: ( border_size: number ) => scaler.textBorderSizeFromDesign( border_size ),
    };
  }

  static isMobile(): boolean { return this.is_mobile; }

  // A brand-new install that hasn't finished the level-1 tutorial yet and
  // hasn't progressed past level 1 — used to route straight into the game
  // screen on launch instead of the menu, so the tutorial can run.
  static isFirstTimePlayer(): boolean
  {
    const stats = StatisticsStorage.load();
    return !stats.tutorial_completed && stats.level === 1;
  }

  public initializeGameScreen(): { screen: GameScreen, ui: GameUI }
  {
    const game_screen = new GameScreen();
    this.game_ui = new GameUI( this.game_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), game_screen );

    return { screen: game_screen, ui: this.game_ui };
  }

  public forgetScreens(): void
  {
    this.game_ui = undefined;
    this.menu_screen = undefined;
    this.leaderboard_screen = undefined;
    this.friends_screen = undefined;
    this.happy_fall_screen = undefined;
  }

  public initializeMenuScreen(): MenuScreen
  {
    const menu_screen = new MenuScreen( this.menu_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );

    this.menu_screen = menu_screen;
    return menu_screen;
  }

  public initializeLeaderboardScreen(): LeaderboardScreen
  {
    const leaderboard_screen = new LeaderboardScreen( this.leaderboard_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );

    this.leaderboard_screen = leaderboard_screen;
    return leaderboard_screen;
  }

  public initializeFriendsScreen(): FriendsScreen
  {
    const friends_screen = new FriendsScreen( this.friends_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );

    this.friends_screen = friends_screen;
    return friends_screen;
  }

  public initializeHappyFallScreen(): HappyFallScreen
  {
    const happy_fall_screen = new HappyFallScreen(
      this.menu_ctx,
      Game.safeAreaLeft(),
      Game.safeAreaTop(),
      Game.getContentWidth(),
      Game.getContentHeight()
    );

    this.happy_fall_screen = happy_fall_screen;
    return happy_fall_screen;
  }

  public openSettingsDialog(): void
  {
    DialogManager.openDialog( new SettingsDialog() );
  }

  public getGameX(): number
  {
    return this.game_x;
  }

  public setGameUICanvasHeight( css_height: number ): boolean
  {
    const dpr = Game.getDpr();
    const target_h = Math.round( dpr * css_height );

    if ( this.game_canvas.height === target_h )
      return false;

    this.game_canvas.height = target_h;
    this.game_canvas.style.height = `${css_height}px`;
    this.game_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    if ( dpr !== 1 )
      this.game_ctx.scale( dpr, dpr );

    return true;
  }

  public allocateScreenCanvas( canvas: HTMLCanvasElement ): void
  {
    const dpr = Game.getDpr();
    const target_w = Math.round( dpr * Game.getScreenWidth() );
    const target_h = Math.round( dpr * Game.getScreenHeight() );

    if ( canvas.width === target_w && canvas.height === target_h )
      return;

    canvas.width = target_w;
    canvas.height = target_h;

    const ctx = canvas.getContext( "2d" );
    if ( ctx )
    {
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      if ( dpr !== 1 )
        ctx.scale( dpr, dpr );
    }
  }

  public releaseScreenCanvas( canvas: HTMLCanvasElement ): void
  {
    if ( canvas.width === 1 && canvas.height === 1 )
      return;

    canvas.width = 1;
    canvas.height = 1;
  }

  public async prepareScreenImages( name: Screens ): Promise<void>
  {
    try
    {
      await ImagesHolder.preloadScreens( [ name ], 10 );
    }
    catch ( e )
    {
      console.warn( `[Game] image preload for screen "${name}" failed`, e );
    }
  }

  public releaseScreenImages( name: Screens ): void
  {
    const keep = [ name, Screens.GAME ];
    if ( name === Screens.FRIENDS || name === Screens.LEADERBOARD )
      keep.push( Screens.MENU );

    ImagesHolder.releaseScreens( keep );
  }

  private updateNavigationCanvas(): void
  {
    const dpr = Game.getDpr();
    const logical_screen_w = Game.getScreenWidth();
    const logical_screen_h = Game.getScreenHeight();

    const height = LayoutScaler.yFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM;

    const target_w = Math.round( dpr * logical_screen_w );
    const target_h = Math.round( dpr * height );

    if ( this.navigation_canvas.width !== target_w || this.navigation_canvas.height !== target_h )
    {
      this.navigation_canvas.width = target_w;
      this.navigation_canvas.height = target_h;
    }

    const y = Game.screenH() - height;

    this.navigation_canvas.style.top = `${y}px`;
    this.navigation_canvas.style.height = `${logical_screen_h - y}px`;

    const ctx = this.navigation_ctx;
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.scale( dpr, dpr );
  }

  private static readonly VFX_HIDE_DELAY_FRAMES = 10;
  private vfx_hide_frames = [ 0, 0, 0, 0, 0 ];
  private static readonly VFX_RELEASE_DELAY_MS = 2000;
  private vfx_hidden_at = [ 0, 0, 0, 0, 0 ];

  private current_screen_painted_once = false;

  private updateVfxLayerVisibility( canvas: HTMLCanvasElement, has_effects: boolean, idx: number ): boolean
  {
    if ( has_effects )
    {
      this.vfx_hide_frames[idx] = 0;
      if ( canvas.width === 1 && canvas.height === 1 )
        this.allocateVfxLayer( canvas, idx );
      if ( canvas.style.display !== "block" )
        canvas.style.display = "block";
      return true;
    }

    if ( canvas.style.display !== "block" )
    {
      this.releaseIdleVfxLayer( canvas, idx );
      return false;
    }

    this.vfx_hide_frames[idx]++;
    if ( this.vfx_hide_frames[idx] < Game.VFX_HIDE_DELAY_FRAMES )
      return true;

    canvas.style.display = "none";
    this.vfx_hidden_at[idx] = performance.now();
    return false;
  }

  private allocateVfxLayer( canvas: HTMLCanvasElement, idx: number ): void
  {
    const dpr = Game.getDpr();
    canvas.width = Math.round( dpr * Game.getScreenWidth() );
    canvas.height = Math.round( dpr * Game.getScreenHeight() );

    if ( idx === 0 )
      WebGLUIRuntime.resize();
    else
      Vfx2DRuntime.handleCanvasResize();
  }

  private releaseIdleVfxLayer( canvas: HTMLCanvasElement, idx: number ): void
  {
    if ( !isMemoryConstrained() || ( canvas.width === 1 && canvas.height === 1 ) )
      return;

    if ( performance.now() - this.vfx_hidden_at[idx] < Game.VFX_RELEASE_DELAY_MS )
      return;

    canvas.width = 1;
    canvas.height = 1;
  }

  private renderAll() // TODO: optimize rendering
  {
    const current_screen = ScreenManager.getCurrentScreen();

    // Background screens keep repainting (button press animations, timers)
    // right when a dialog's layers are being composited for the first time —
    // exactly the most bandwidth-hungry frames. They sit behind the dim, so
    // their repaints can wait out the dialog open window; dirty flags survive.
    // The freeze must never swallow the FIRST paint of a freshly shown screen:
    // a dialog opened together with a screen change (rate dialog on the menu,
    // day-completed dialog on daily challenge) would otherwise hold a blank
    // canvas for 400ms, flashing the bare body background under the dialog.
    const dialog_open_freeze = current_screen !== Screens.MENU
      && DialogManager.isAnyDialogOpened()
      && DialogManager.msSinceLastOpen() < 400
      && this.current_screen_painted_once;

    if ( !dialog_open_freeze && current_screen !== null )
      this.current_screen_painted_once = true;

    if ( !dialog_open_freeze )
    if ( current_screen === Screens.GAME ) this.game_ui?.render(); else
    if ( current_screen === Screens.MENU ) this.menu_screen?.render(); else
    if ( current_screen === Screens.LEADERBOARD ) this.leaderboard_screen?.render(); else
    if ( current_screen === Screens.FRIENDS ) this.friends_screen?.render(); else
    if ( current_screen === Screens.HAPPY_FALL ) this.happy_fall_screen?.render();

    if ( !dialog_open_freeze && this.navigation_canvas.style.display === "block" )
      this.navigation_block?.render();
    
    if ( this.updateVfxLayerVisibility( this.webgl_ui_main_canvas, WebGLUIRuntime.hasMainElements(), 0 ) )
      WebGLUIRuntime.renderMain( Game.dots_per_pixel );
    
    if ( this.updateVfxLayerVisibility( this.vfx_main_ui_back_canvas, Vfx2DRuntime.hasMainBackEffects(), 1 ) )
      Vfx2DRuntime.renderMainBack( Game.dots_per_pixel );
    
    if ( this.updateVfxLayerVisibility( this.vfx_main_ui_front_canvas, Vfx2DRuntime.hasMainFrontEffects(), 2 ) )
      Vfx2DRuntime.renderMainFront( Game.dots_per_pixel );

    if ( this.updateVfxLayerVisibility( this.vfx_overlay_back_canvas, Vfx2DRuntime.hasOverlayBackEffects(), 3 ) )
      Vfx2DRuntime.renderOverlayBack( Game.dots_per_pixel );

    if ( this.updateVfxLayerVisibility( this.vfx_overlay_front_canvas, Vfx2DRuntime.hasOverlayFrontEffects(), 4 ) )
      Vfx2DRuntime.renderOverlayFront( Game.dots_per_pixel );

    if ( this.tooltip_canvas.style.display !== "none" )
      TooltipManager.render();

    DialogManager.render();
    OverlayManager.render();
  }

  private animationsUpdateAll( now: number )
  {
    const current_screen = ScreenManager.getCurrentScreen();

    if ( current_screen === Screens.GAME ) this.game_ui?.update( now ); else
    if ( current_screen === Screens.MENU ) this.menu_screen?.update( now ); else
    if ( current_screen === Screens.LEADERBOARD ) this.leaderboard_screen?.update( now ); else
    if ( current_screen === Screens.FRIENDS ) this.friends_screen?.update( now ); else
    if ( current_screen === Screens.HAPPY_FALL ) this.happy_fall_screen?.update( now );

    this.navigation_block?.update( now );
    OverlayManager.update( now );
    DialogManager.update( now );
    TooltipManager.update( now );
    WebGLUIRuntime.update( now );
    Vfx2DRuntime.update( now );
  }

  static screenW() { return this.SCREEN_WIDTH; }
  static screenH() { return this.SCREEN_HEIGHT; }

  private static game_ui_paint_revision = 0;

  static markGameUIPainted(): void { this.game_ui_paint_revision++; }
  static gameUIPaintRevision(): number { return this.game_ui_paint_revision; }

  static getContentWidth(): number { return this.SCREEN_WIDTH - this.SAFE_AREA_LEFT - this.SAFE_AREA_RIGHT; }
  static getContentHeight(): number { return this.SCREEN_HEIGHT - this.SAFE_AREA_TOP - this.SAFE_AREA_BOTTOM; }

  public showNavigationBlock()
  {
    this.navigation_canvas.style.display = "block";
  }

  public hideNavigationBlock()
  {
    this.navigation_canvas.style.display = "none";
  }

  // A tooltip raised above the navigation swallows the tap that closes it;
  // when that tap landed on the bar, the bar should still receive it.
  private replayTapOnNavigation( p: Vector2 ): boolean
  {
    const block = this.navigation_block;
    const canvas = this.navigation_canvas;
    if ( !block || canvas.style.display !== "block" )
      return false;

    const offset_x = parseFloat( canvas.style.left || "0" ) - parseFloat( this.tooltip_canvas.style.left || "0" );
    const offset_y = parseFloat( canvas.style.top || "0" ) - parseFloat( this.tooltip_canvas.style.top || "0" );
    const local = new Vector2( p.x - offset_x, p.y - offset_y );
    if ( local.y < 0 || local.y > parseFloat( canvas.style.height || "0" ) )
      return false;

    const now = performance.now();
    block.onTouchStart( local, now );
    block.onTouchEnd( local, now );
    return true;
  }

  public setNavigationClickable( clickable: boolean ): void
  {
    this.navigation_block?.setClickable( clickable );
  }
  
  public updateNavigationBlock(): void
  {
    this.navigation_block?.updateScreenBlock();
  }

  public async init(): Promise<void>
  {
    fillAllImagesInfo();
    fillAllResourcesInfo();

    await PhysicsWorld.init();
    await NineSliceDrawer.init();
    // await AssetsSizeLoader.load();

    await ImagesHolder.preloadImages( [
      ImgID.UI_LoadingBg,
      ImgID.UI_LoadingProgressBarBase,
      ImgID.UI_LoadingProgressBarFill,
      ImgID.UI_LoadingLogo,
      ImgID.UI_CapsuleBlue,
      ImgID.AvatarCircle,
      ImgID.UI_ConnectionFailedIcon,
      ImgID.UI_GreenFrameBtn,
      ImgID.UI_SettingsDialogCloseBtn,
    ] );

    DialogManager.init( this.dialog_ctx, Game.getScreenWidth(), Game.getScreenHeight() );
    TooltipManager.init(
      this.tooltip_ctx,
      Game.SCREEN_WIDTH,
      Game.SCREEN_HEIGHT,
      p =>
      {
        if ( !this.replayTapOnNavigation( p ) )
          ScreenManager.replayTapOnCurrentUi( p, this.tooltip_canvas );
      }
    );
    NetworkManager.onUiReady();

    if ( ( window as any ).zero_loading_ref )
    {
      cancelAnimationFrame( ( window as any ).zero_loading_ref );
      ( window as any ).zero_loading_ref = null;
    }

    this.setupLoadingCanvas( true );
    this.loading_screen = new LoadingScreen(
      this.loading_ctx,
      0,
      0,
      Game.getScreenWidth(),
      Game.getScreenHeight() + Game.safeAreaBottom()
    );
    this.loading_screen.render();
    const loading_screen_started_at = performance.now();

    // this.startRenderLoop();

    const scene = SceneManager.init();
    Renderer.init();

    const cameraController = new CameraController();
    SceneManager.camera = cameraController.getCamera();

    const FIXED_DT = 1 / 60;
    const STEP_SLACK = 0.001;
    let accumulator = 0;
    let last_stats_log = 0;
    let last_frame_time = -1;

    FpsOverlay.init();
    FpsOverlay.setCurrentScreenProvider( () => ScreenManager.getCurrentScreen() ?? "none" );

    const loop = ( vsync?: number ) =>
    {
      requestAnimationFrame( loop );

      if ( TriangleBench.isActive() )
        return;

      const frame_start = performance.now();

      // getDelta() викликаємо завжди, щоб dt не накопичувався в кліку
      const clock_dt = Renderer.clock.getDelta();
      const frame_time = vsync ?? frame_start;
      const dt = Math.min( last_frame_time >= 0 ? ( frame_time - last_frame_time ) / 1000 : clock_dt, 0.1 );
      last_frame_time = frame_time;

      let steps = 0;

      if ( !SimulationContext.enabled && PhysicsWorld.isThreaded() )
      {
        accumulator = Math.min( accumulator + dt, FIXED_DT * 4 );
        PhysicsWorld.resetTimers();
        Game.physics_slow_mode = false;

        if ( PhysicsWorld.pumpThreaded() )
          steps = 1;
        PhysicsDebugRenderer.update();
        FpsOverlay.mark( "phys", performance.now() - frame_start );

        const t_screen = performance.now();
        ScreenManager.update( dt );

        const wants_step = accumulator >= FIXED_DT - STEP_SLACK;
        const can_step = wants_step && PhysicsWorld.canStepThreaded();
        if ( can_step )
        {
          const substeps = accumulator >= FIXED_DT * 2 - STEP_SLACK ? 2 : 1;
          const step_dt = FIXED_DT * substeps;
          PhysicsWorld.stepThreaded( step_dt, substeps );
          accumulator = Math.max( -STEP_SLACK, accumulator - step_dt );
        }
        PhysicsWorld.noteThreadedFrame( dt, wants_step && !can_step );
        FpsOverlay.mark( "game", performance.now() - t_screen );
        if ( FpsOverlay.isCollecting() )
          FpsOverlay.markPhysics( PhysicsWorld.step_ms, PhysicsWorld.hook_ms, PhysicsWorld.counts() );

        PhysicsInterpolation.onPhysicsFrame( steps, accumulator / FIXED_DT );
      }
      else if ( !SimulationContext.enabled ) // [SIM]
      {
        accumulator += dt;
        PhysicsWorld.resetTimers();

        const physics_start = performance.now();
        const step_dt = Game.physics_slow_mode ? FIXED_DT * 2 : FIXED_DT;

        while ( accumulator >= step_dt && steps < Game.max_physics_steps )
        {
          PhysicsWorld.update( step_dt );
          accumulator -= step_dt;
          steps++;

          if ( performance.now() - physics_start >= Game.physics_budget_ms )
            break;
        }

        PhysicsWorld.noteMainFrame( steps );

        if ( Game.physics_adaptive && steps > 0 )
        {
          const per_step = PhysicsWorld.step_ms / steps;

          if ( per_step >= Game.physics_slow_enter_ms )
            Game.physics_slow_mode = true;
          else if ( per_step <= Game.physics_slow_exit_ms )
            Game.physics_slow_mode = false;
        }

        // A long frame (system stall, GC, GPU hitch) leaves the accumulator in
        // debt. Paying it all back in the next frame makes that frame long too,
        // which snowballs — so the debt is dropped instead.
        if ( accumulator >= step_dt )
          accumulator = 0;

        PhysicsInterpolation.onPhysicsFrame( steps, accumulator / ( Game.physics_slow_mode ? FIXED_DT * 2 : FIXED_DT ) );

        PhysicsDebugRenderer.update();

        FpsOverlay.mark( "phys", performance.now() - frame_start );
        if ( FpsOverlay.isCollecting() )
          FpsOverlay.markPhysics( PhysicsWorld.step_ms, PhysicsWorld.hook_ms, PhysicsWorld.counts() );

        const t_screen = performance.now();
        ScreenManager.update( dt );
        FpsOverlay.mark( "game", performance.now() - t_screen );
      }
      else
      {
        accumulator = 0; // [SIM] щоб не було "черги" кроків після симуляції
      }

      const now = performance.now();

      if ( this.loading_screen )
      {
        const total = ImagesHolder.TOTAL_COUNT + ResourceManager.getTotalCount();
        const loaded = ImagesHolder.LOADED_COUNT + ResourceManager.getDownloadedCount();

        this.loading_screen.setProgress( total > 0 ? loaded / total : 0 );
        this.loading_screen.update( now );
        this.loading_screen.render();
      }

      if ( this.loading_game_screen )
      {
        this.loading_game_screen.setProgress( 0 );
        this.loading_game_screen.update( now );
        this.loading_game_screen.render();
      }

      const t_ui = performance.now();
      this.animationsUpdateAll( now );
      this.renderAll();
      FpsOverlay.mark( "ui2d", performance.now() - t_ui );

      const t_gl = performance.now();
      if ( ScreenManager.getCurrentScreen() === Screens.GAME && !this.isSceneRevealHeld( now ) )
      {
        PhysicsInterpolation.apply();
        Renderer.render( scene, SceneManager.camera );
        PhysicsInterpolation.restore();
        this.scene_revealed = true;
      }
      FpsOverlay.mark( "gl", performance.now() - t_gl );

      FpsOverlay.markSteps( steps );
      FpsOverlay.tick( performance.now(), vsync );
    };

    loop();

    await this.loadStartupResourcesWithRetry();
    Vfx2DGlobalAtlas.init( VFX_2D_ATLAS_CONFIG, VFX_2D_ATLAS_SIZE );
    WebGLUIRuntime.setup();
    await Vfx2DRuntime.setup();

    Renderer.compile( SceneManager.scene, SceneManager.camera );

    this.updateNavigationCanvas();

    OverlayManager.init( this.overlay_ctx, Game.getScreenWidth(), Game.getScreenHeight() );

    const navigation_height = LayoutScaler.heightFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM;
    const navigation_y = LayoutScaler.yFromDesign( 0 );

    this.navigation_block = new NavigationBlock(
      this.navigation_ctx,
      Game.SAFE_AREA_LEFT,
      navigation_y,
      Game.getContentWidth(),
      navigation_height
    )

    const ENTRANCE_BUFFER_MS = 100;
    const MAX_ENTRANCE_WAIT_MS = 3000;

    const entrance_remaining = LoadingScreen.ENTRANCE_DURATION_MS + ENTRANCE_BUFFER_MS - ( performance.now() - loading_screen_started_at );
    const entrance_wait = Math.min( Math.max( entrance_remaining, 0 ), MAX_ENTRANCE_WAIT_MS );
    if ( entrance_wait > 0 )
      await new Promise( r => setTimeout( r, entrance_wait ) );

    this.loading_screen = undefined;
    this.loading_canvas.style.display = 'none';
    if ( isMemoryConstrained() )
      this.releaseScreenCanvas( this.loading_canvas );

    void ResourceManager.startLoading();
    void ImagesHolder.preloadDeferred( 2 );
    void DialogManager.prewarmDialogs( [
      () => new SettingsDialog(),
      () => new AvatarSelectionDialog( () => {} ),
      () => new ProfileDialog(),
      () => new PlayDialog( StatisticsStorage.load().level ),
      () => new MoreLivesDialog(),
      () => new LeaveLevelDialog(),
    ] );
    void ImagesHolder.warmupGpu( [
      ...Array.from( { length: 20 }, ( _, i ) => AvatarUtils.getAvatarImgId( i ) ),
      ImgID.AvatarCircle,
      ImgID.UI_GreenFrameBtn,
      ImgID.UI_GrayBtn,
      ImgID.IconAvatarSelected,
      ImgID.UI_SettingsDialogCloseBtn,
      ImgID.UI_CapsuleBlue,
    ] );

    // A batch from the editor runs headless and never shows a screen at all.
    if ( TestLevelBridge.isBatchActive() )
    {
      if ( TestLevelBridge.isCollector() )
        runSimulationCollector();
      else if ( TestLevelBridge.isSimBatch() )
        void runSimulationBatch();
      else
        void runStabilityBatch();
      return;
    }

    // A level handed over by the editor skips the menu and starts straight away.
    // Big (cube-shaped) levels no longer fit in the URL — the editor sends a
    // "pending" marker instead and posts the real payload once we say we're
    // ready; wait for that handshake before reading the level back out.
    if ( TestLevelBridge.isPendingSingle() )
      await TestLevelBridge.awaitPendingSingle();

    const test_level = TestLevelBridge.getLevel();
    if ( test_level !== null )
      ScreenManager.showScreen( Screens.GAME, { level: test_level } );
    else if ( Game.isFirstTimePlayer() )
      ScreenManager.showScreen( Screens.GAME, { level: 1 } );
    else
    {
      await this.menu_startup_ready;
      ScreenManager.showScreen( Screens.MENU );
    }

    audioController.startBackgroundLoading();
    console.log('Loaded bytes:', "images:", ImagesHolder.getDownloadedBytes(), "resources:", ResourceManager.getDownloadedBytes(), "audio:", audioController.getDownloadedBytes());
  }

  private async loadStartupResourcesWithRetry(): Promise<void>
  {
    let delay_ms = 2_000;

    for ( let attempt = 1; ; attempt++ )
    {
      try
      {
        await Promise.all( [
          ImagesHolder.preloadScreens( [ Screens.MENU, Screens.GAME ], 32, true ),
          ImagesHolder.preloadImages( VFX_2D_ATLAS_CONFIG ),
          FontLoader.preloadAll(),
          audioController.loadEssential(),
          LocalizationManager.whenRemoteReady(),
        ] );

        const missing_images = ImagesHolder.getMissingImageIds( [ Screens.MENU, Screens.GAME ] );
        if ( missing_images.length > 0 )
          throw new Error( `Failed to load ${missing_images.length} images (first id: ${missing_images[0]})` );

        if ( attempt > 1 )
          console.info( `[LoadRetry] startup resources loaded on attempt ${attempt}` );
        return;
      }
      catch ( error )
      {
        const network_error = !NetworkManager.isOnline() || isNetworkishError( error );
        if ( network_error )
          NetworkManager.handleNetworkError( error );
        else
          NetworkManager.handleLoadingDataError( error );

        if ( network_error && !NetworkManager.isOfflinePlayable() )
          await NetworkManager.waitForOnline();
        await new Promise( resolve => window.setTimeout( resolve, delay_ms ) );
        delay_ms = Math.min( delay_ms * 2, 15_000 );
      }
    }
  }

  private setupLoadingCanvas( include_bottom_safe_area: boolean = false ): void
  {
    const dpr = Game.getDpr();
    const logical_screen_w = Game.getScreenWidth();
    const logical_screen_h = Game.getScreenHeight()
      + ( include_bottom_safe_area ? Game.safeAreaBottom() : 0 );

    // Assigning width/height throws the backing store away and allocates a new
    // one even when the value is unchanged — a full-screen buffer at device
    // pixel ratio, on every level transition. Only pay that when the size has
    // actually moved; otherwise reset and clear the existing surface by hand.
    const target_w = Math.round( dpr * logical_screen_w );
    const target_h = Math.round( dpr * logical_screen_h );
    const ctx = this.loading_canvas.getContext( '2d' ) as CanvasRenderingContext2D;

    if ( this.loading_canvas.width !== target_w || this.loading_canvas.height !== target_h )
    {
      this.loading_canvas.width = target_w;
      this.loading_canvas.height = target_h;
    }
    else
    {
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      ctx.clearRect( 0, 0, target_w, target_h );
    }

    this.loading_canvas.style.left = `${this.game_x}px`;
    this.loading_canvas.style.top = '0px';
    this.loading_canvas.style.width = `${logical_screen_w}px`;
    this.loading_canvas.style.height = `${logical_screen_h}px`;
    this.loading_canvas.style.display = 'block';

    this.loading_ctx = ctx;

    if ( dpr !== 1 )
      this.loading_ctx.scale( dpr, dpr );
  }

  public showLoadingGameScreen( preserve_vfx: boolean = false, variant: LoadingGameVariant = "default" ): void
  {
    this.loading_game_screen_shown_at = performance.now();
    ScreenManager.hideAll( preserve_vfx );
    this.hideNavigationBlock();
    this.setupLoadingCanvas();

    LoadingGameScreen.setVariant( variant );
    LoadingGameScreen.rollAnimal();
    this.loading_game_screen = new LoadingGameScreen(
      this.loading_ctx,
      0,
      0,
      Game.getScreenWidth(),
      Game.getScreenHeight()
    );

    const content = new UIElementHolder(
      this.loading_ctx,
      Game.safeAreaLeft(),
      Game.safeAreaTop(),
      Game.getContentWidth(),
      Game.getContentHeight()
    );
    content.setClickable( false );
    this.loading_game_screen.addChild( content );
    addActiveCoinTransitionBadge( content, this.loading_ctx );

    this.loading_game_screen.update( performance.now() );
    this.loading_game_screen.render();
  }

  public waitForLoadingGamePlay(): Promise<boolean>
  {
    const screen = this.loading_game_screen;
    if ( !screen?.isConveyor() )
      return Promise.resolve( true );

    return new Promise( resolve =>
    {
      this.loading_game_play = resolve;
      screen.showPlayButtons( play => this.chooseLoadingGamePlay( play ) );
    } );
  }

  private chooseLoadingGamePlay( play: boolean ): void
  {
    const resolve = this.loading_game_play;
    this.loading_game_play = null;
    resolve?.( play );
  }

  public hideLoadingGameScreen(): void
  {
    this.loading_game_screen?.cleanup();
    this.loading_game_screen = undefined;
    this.loading_game_play = null;
    this.loading_canvas.style.display = "none";
    if ( isMemoryConstrained() )
      this.releaseScreenCanvas( this.loading_canvas );
  }

  public isLoadingGameScreenShown(): boolean
  {
    return this.loading_game_screen !== undefined;
  }

  public holdSceneRevealUntil( time_ms: number ): void
  {
    const now = performance.now();
    this.scene_reveal_hold_until = Math.min(
      Math.max( now, time_ms ),
      now + Game.MAX_SCENE_REVEAL_HOLD_MS
    );
    this.scene_revealed = false;
  }

  private isSceneRevealHeld( now: number ): boolean
  {
    return !this.scene_revealed
      && this.loading_game_screen !== undefined
      && now < this.scene_reveal_hold_until;
  }

  public waitForSceneRevealed( timeout_ms: number = 3_000 ): Promise<void>
  {
    if ( this.scene_revealed )
      return Promise.resolve();

    return new Promise( resolve =>
    {
      let settled = false;
      const finish = () =>
      {
        if ( settled ) return;
        settled = true;
        window.clearTimeout( timeout_id );
        resolve();
      };
      const check = () =>
      {
        if ( settled ) return;
        if ( this.scene_revealed )
        {
          finish();
          return;
        }
        requestAnimationFrame( check );
      };
      const timeout_id = window.setTimeout( finish, timeout_ms );
      requestAnimationFrame( check );
    } );
  }

  public waitForLoadingGameScreenHidden( timeout_ms: number = 10_000 ): Promise<void>
  {
    if ( this.loading_game_screen === undefined )
      return Promise.resolve();

    return new Promise( resolve =>
    {
      let settled = false;
      const finish = () =>
      {
        if ( settled ) return;
        settled = true;
        window.clearTimeout( timeout_id );
        resolve();
      };
      const check = () =>
      {
        if ( settled ) return;
        if ( this.loading_game_screen === undefined )
        {
          finish();
          return;
        }
        requestAnimationFrame( check );
      };
      const timeout_id = window.setTimeout( finish, timeout_ms );
      requestAnimationFrame( check );
    } );
  }

  public waitForLoadingGameScreenPaint(): Promise<void>
  {
    return new Promise( resolve => requestAnimationFrame( () =>
      requestAnimationFrame( () => resolve() )
    ) );
  }

  public waitForNextGameUIPaint( timeout_ms: number = 1_000 ): Promise<void>
  {
    const revision = Game.gameUIPaintRevision();

    return new Promise( resolve =>
    {
      let settled = false;
      const finish = () =>
      {
        if ( settled ) return;
        settled = true;
        window.clearTimeout( timeout_id );
        resolve();
      };
      const check = () =>
      {
        if ( settled ) return;
        if ( Game.gameUIPaintRevision() > revision )
        {
          finish();
          return;
        }
        requestAnimationFrame( check );
      };
      const timeout_id = window.setTimeout( finish, timeout_ms );
      requestAnimationFrame( check );
    } );
  }

  public async waitForMinimumLoadingGameScreenDuration(): Promise<void>
  {
    const min_duration_ms = 1500;
    const elapsed = performance.now() - this.loading_game_screen_shown_at;
    const remaining = Math.max( 0, min_duration_ms - elapsed );
    if ( remaining > 0 )
      await new Promise<void>( resolve => setTimeout( resolve, remaining ) );
  }

  static getYOverflow() { return this.Y_OVERFLOW; }

  public initBackButton(): void
  {
    BackButtonHandler.getInstance().init();
  }

  public setFpsOverlayEnabled( enabled: boolean ): void
  {
    FpsOverlay.setEnabled( enabled );
  }

  public startRenderLoop()
  {
    let prev_t = performance.now();
    const MIN_DT = 1000 / GlobalSettings.RENDERING_MAX_FPS;

    const loop = () =>
    {
      try
      {
        const now = performance.now();
        const dt = now - prev_t;

        if ( dt >= MIN_DT )
        {
          prev_t = now;

          // Оновлення логіки та рендер
          this.animationsUpdateAll( now );
          this.renderAll();
        }

        requestAnimationFrame( loop );
      }
      catch ( e )
      {
        console.error( e );
        crashCapture( e, { phase: 'render_loop' }, false );
      }
    };

    requestAnimationFrame( loop );
  }
}
